import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { mockData } from './mock';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-lg shadow-lg' : 'bg-white/90 backdrop-blur-sm'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/"
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <div className="flex-shrink-0">
              <img 
                src={mockData.company.logo} 
                alt="TSI Logo"
                className="w-16 h-16 object-contain"
                onError={(e) => {
                  // Fallback to initials if logo fails to load
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg items-center justify-center hidden">
                <span className="text-white font-bold text-xl">TSI</span>
              </div>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-gray-900">Traffic Service</h1>
              <p className="text-sm text-gray-600">International</p>
            </div>
          </Link>

          {/* Desktop Navigation - Centered */}
          <nav className="hidden lg:flex items-center space-x-8 absolute left-1/2 transform -translate-x-1/2">
            {/* <Link 
              to="/services"
              className={`transition-colors font-medium ${
                location.pathname === '/services' 
                  ? 'text-blue-600' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Services
            </Link> */}
            <Link 
              to="/projects"
              className={`transition-colors font-medium ${
                location.pathname === '/projects' 
                  ? 'text-blue-600' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Our Projects
            </Link>
            <Link 
              to="/solutions"
              className={`transition-colors font-medium ${
                location.pathname === '/solutions' 
                  ? 'text-blue-600' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Solutions
            </Link>
            <Link 
              to="/about"
              className={`transition-colors font-medium ${
                location.pathname === '/about' 
                  ? 'text-blue-600' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              About Us
            </Link>
            <Link 
              to="/career"
              className={`transition-colors font-medium ${
                location.pathname === '/career' 
                  ? 'text-blue-600' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Career
            </Link>
            <Link 
              to="/contact"
              className={`transition-colors font-medium ${
                location.pathname === '/contact' 
                  ? 'text-blue-600' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/contact">
              <Button 
                className="text-white px-6 py-2 rounded-lg transition-all duration-300 hover:scale-105"
                style={{ 
                  background: 'linear-gradient(to right, rgb(6 47 136), rgb(99 102 241))' 
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(to right, rgb(6 47 136), rgb(147 51 234))';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(to right, rgb(6 47 136), rgb(99 102 241))';
                }}
              >
                Get Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200 py-4">
            <nav className="flex flex-col space-y-4">
              <Link 
                to="/services"
                onClick={handleNavClick}
                className={`text-left px-4 py-2 rounded-lg transition-colors ${
                  location.pathname === '/services' 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                Services
              </Link>
              <Link 
                to="/projects"
                onClick={handleNavClick}
                className={`text-left px-4 py-2 rounded-lg transition-colors ${
                  location.pathname === '/projects' 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                Projects
              </Link>
              <Link 
                to="/solutions"
                onClick={handleNavClick}
                className={`text-left px-4 py-2 rounded-lg transition-colors ${
                  location.pathname === '/solutions' 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                Solutions
              </Link>
              <Link 
                to="/about"
                onClick={handleNavClick}
                className={`text-left px-4 py-2 rounded-lg transition-colors ${
                  location.pathname === '/about' 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                About
              </Link>
              <Link 
                to="/career"
                onClick={handleNavClick}
                className={`text-left px-4 py-2 rounded-lg transition-colors ${
                  location.pathname === '/career' 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                Career
              </Link>
              <Link 
                to="/contact"
                onClick={handleNavClick}
                className={`text-left px-4 py-2 rounded-lg transition-colors ${
                  location.pathname === '/contact' 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                Contact
              </Link>
              <div className="px-4 pt-4 border-t">
                <Link to="/contact" onClick={handleNavClick}>
                  <Button 
                    className="w-full text-white py-3 rounded-lg transition-all duration-300 hover:scale-105"
                    style={{ 
                      background: 'linear-gradient(to right, rgb(6 47 136), rgb(99 102 241))' 
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(to right, rgb(6 47 136), rgb(147 51 234))';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(to right, rgb(6 47 136), rgb(99 102 241))';
                    }}
                  >
                    Get Quote
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
      {/* Bottom Gradient Line */}
      <div className="w-full h-1" style={{ backgroundColor: 'rgb(6 47 136)' }}></div>
    </header>
  );
};